import type { PlatformSection } from "@/shared/platform-activity/platformActivity";

export const OTC_METRICS_PERIODS = [7, 30, 90] as const;
export type OtcMetricsPeriod = (typeof OTC_METRICS_PERIODS)[number];

export interface OtcMetricsWindow {
  from: string;
  to: string;
}

export interface OtcDuration {
  p50_seconds: number;
  p90_seconds: number;
  samples: number;
}

export interface OtcNotCompleted {
  count: number;
  /** Fraction (0-1) of the operations that ended in the window and did not complete. */
  rate: number | null;
  /** Lower-cased final status: failed, cancelled, reversed, expired, refunding, refunded. */
  by_reason: Record<string, number>;
}

/** Per user, not per quote: a user who quoted ten times and traded once converted. */
export interface OtcConversion {
  quoted_users: number;
  converted_users: number;
  rate: number | null;
  max_quoted_volume: string;
  max_quoted_asset_volume: string;
  traded_volume: string;
  traded_asset_volume: string;
  /** Traded / largest quoted, can exceed 1. */
  capture_rate: number | null;
}

export interface OtcPeriodMetrics {
  transactions: number;
  volume: string;
  asset_volume: string;
  average_ticket: string | null;
  average_asset_ticket: string | null;
  /** The client's price per USDT in COP, fees included; null on the total. */
  average_price: string | null;
  users: number;
  new_users: number;
  organizations: number;
  transactions_per_user: number | null;
  duration: OtcDuration | null;
  not_completed: OtcNotCompleted;
  conversion: OtcConversion | null;
}

export interface OtcMetricsChange {
  transactions: number | null;
  volume: number | null;
  asset_volume: number | null;
  average_ticket: number | null;
  average_asset_ticket: number | null;
  average_price: number | null;
  users: number | null;
  new_users: number | null;
  transactions_per_user: number | null;
  duration_p50: number | null;
  /** Percentage points, not a percent change. */
  not_completed_rate: number | null;
  /** Percentage points, not a percent change. */
  conversion_rate: number | null;
}

export interface OtcQuoter {
  user_id: string;
  name: string | null;
  email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  quotes: number;
  max_quoted_volume: string;
  max_quoted_asset_volume: string;
  /** 0 = quoted and did not trade. */
  transactions: number;
  traded_volume: string;
  traded_asset_volume: string;
  last_quoted_at: string;
}

export interface OtcTopUser {
  user_id: string | null;
  name: string | null;
  email: string | null;
  organization_id: string;
  organization_name: string;
  transactions: number;
  volume: string;
  asset_volume: string;
  average_ticket: string;
}

export interface OtcSideMetrics {
  current: OtcPeriodMetrics;
  previous: OtcPeriodMetrics;
  change: OtcMetricsChange;
  top_users: OtcTopUser[];
  quoters: OtcQuoter[];
}

export interface OtcDayValues {
  transactions: number;
  volume: string;
  asset_volume: string;
  average_price: string | null;
}

export interface OtcDay {
  date: string;
  buy: OtcDayValues;
  sell: OtcDayValues;
  card_top_up: OtcDayValues;
}

/** The organization a metrics read was scoped to. */
export interface MetricsOrganizationRef {
  id: string;
  name: string | null;
}

export interface OtcMonthValues {
  transactions: number;
  volume: string;
  asset_volume: string;
}

/** One calendar month in Colombia (`YYYY-MM`). */
export interface OtcMonth {
  month: string;
  buy: OtcMonthValues;
  sell: OtcMonthValues;
  card_top_up: OtcMonthValues;
}

export interface OtcMetrics {
  days: number;
  current: OtcMetricsWindow;
  previous: OtcMetricsWindow;
  currency: string;
  asset: string;
  /** The organization every figure is scoped to; null for every organization. */
  organization: MetricsOrganizationRef | null;
  buy: OtcSideMetrics;
  sell: OtcSideMetrics;
  /** Card loads from the COP wallet: purchases of their own, never quoted on the desk, so no conversion. */
  card_top_up: OtcSideMetrics;
  /** Purchases, sales and card loads together. */
  total: OtcSideMetrics;
  daily: { current: OtcDay[]; previous: OtcDay[] };
  /** The last 12 calendar months, oldest first, the current one in progress. */
  monthly: OtcMonth[];
}

/** Money moved through the payment wallets. Every `*_usd` is at the rate of the operation's own day. */
export type TpvProduct = "collection" | "otc_sell" | "payout" | "otc_buy" | "card_top_up";

export interface TpvRevenue {
  total_usd: string;
  /** Coloca's commission on the operation: no provider fee passed through, no tax. */
  service_usd: string;
  /** Conversion fee of cross-currency payouts and the fee of every swap. */
  conversion_usd: string;
  /** Desk execution result of USDT purchases; pending finance sign-off. */
  fx_execution_usd: string;
}

export interface TpvNotCompleted {
  count: number;
  rate: number | null;
  /** Lower-cased final status: rejected, failed, reversed, cancelled. */
  by_reason: Record<string, number>;
}

export interface TpvPeriodMetrics {
  transactions: number;
  volume_usd: string;
  average_ticket_usd: string | null;
  revenue: TpvRevenue;
  /** Revenue / volume, 0-1. */
  take_rate: number | null;
  organizations: number;
  new_organizations: number;
  not_completed: TpvNotCompleted;
  /** Collections nobody paid that expired; not part of `not_completed`. */
  expired: number;
  /** Operations whose currency had no known rate, left out of every dollar figure. */
  unconverted_transactions: number;
}

export interface TpvMetricsChange {
  transactions: number | null;
  volume_usd: number | null;
  average_ticket_usd: number | null;
  revenue_usd: number | null;
  /** Percentage points, not a percent change. */
  take_rate: number | null;
  organizations: number | null;
  new_organizations: number | null;
  /** Percentage points, not a percent change. */
  not_completed_rate: number | null;
}

export interface TpvFlowMetrics {
  current: TpvPeriodMetrics;
  previous: TpvPeriodMetrics;
  change: TpvMetricsChange;
}

export interface TpvInFlightValues {
  transactions: number;
  volume_usd: string;
}

export interface TpvInFlight {
  payins: TpvInFlightValues;
  payouts: TpvInFlightValues;
}

export interface TpvCorridorValues {
  transactions: number;
  /** In `source_currency`. */
  volume: string;
  volume_usd: string;
  revenue_usd: string;
}

export interface TpvCorridor {
  direction: "in" | "out";
  product: TpvProduct;
  rail: string;
  country: string;
  /** Collected, or delivered on a payout. */
  currency: string;
  /** Of the wallet credited or debited. */
  source_currency: string;
  current: TpvCorridorValues;
  previous: TpvCorridorValues;
  /** Share of the current TPV, 0-1. */
  share: number | null;
  change: number | null;
}

export interface TpvFxPair {
  origin_currency: string;
  destination_currency: string;
  current: TpvCorridorValues;
  previous: TpvCorridorValues;
  change: number | null;
}

/** One organization that operated in either window, as the ranking lists it. */
export interface TpvOrganization {
  organization_id: string;
  name: string | null;
  payins_usd: string;
  payouts_usd: string;
  tpv_usd: string;
  /** Its swaps; not part of the TPV. */
  fx_usd: string;
  /** Confirmed collections. */
  transactions_in: number;
  /** Paid payouts. */
  transactions_out: number;
  transactions: number;
  revenue_usd: string;
  share: number | null;
  previous_tpv_usd: string;
  change: number | null;
}

export interface TpvCustodyCurrency {
  currency: string;
  wallets: number;
  available: string;
  pending: string;
  blocked: string;
  available_usd: string | null;
  pending_usd: string | null;
  blocked_usd: string | null;
  x_per_usd: string | null;
  /** Ledger credits minus debits of the window, in `currency`. */
  net_change: { current: string; previous: string };
}

export interface TpvCustody {
  as_of: string;
  wallets: number;
  new_wallets: { current: number; previous: number };
  active_wallets: { current: number; previous: number };
  available_usd: string;
  pending_usd: string;
  blocked_usd: string;
  by_currency: TpvCustodyCurrency[];
}

export interface TpvDay {
  date: string;
  payins_usd: string;
  payouts_usd: string;
  fx_usd: string;
  transactions_in: number;
  transactions_out: number;
  revenue_usd: string;
}

export interface TpvRates {
  /** Share (0-1) of the days with operations that leaned on another day's rate. */
  filled_share: number | null;
  missing_currencies: string[];
  latest_rate_at: string | null;
}

export interface TpvMetrics {
  days: number;
  current: OtcMetricsWindow;
  previous: OtcMetricsWindow;
  currency: string;
  /** The organization every figure is scoped to; null for all of them. */
  organization: { id: string; name: string | null } | null;
  /** Collections plus payouts; its revenue also counts the fees of swaps. */
  total: TpvFlowMetrics;
  payins: TpvFlowMetrics;
  payouts: TpvFlowMetrics;
  fx: TpvFlowMetrics;
  top_ups: TpvFlowMetrics;
  in_flight: TpvInFlight;
  corridors: TpvCorridor[];
  fx_pairs: TpvFxPair[];
  organizations: TpvOrganization[];
  custody: TpvCustody;
  daily: { current: TpvDay[]; previous: TpvDay[] };
  rates: TpvRates;
}

export type ExperimentStatus = "RUNNING" | "PAUSED" | "ROLLED_OUT" | (string & {});

export interface ExperimentVariant {
  key: string;
  weight: number;
}

export interface Experiment {
  key: string;
  description: string;
  status: ExperimentStatus;
  variants: ExperimentVariant[];
  control_variant: string;
  rollout_variant: string;
  started_at: string;
  ended_at: string | null;
  users_by_variant: { variant: string; users: number }[];
}

export interface ExperimentGroupResult {
  variant: string;
  users: number;
  exposures: number;
  converters: number;
  conversions: number;
  conversion_rate: number | null;
  value_total: string;
  average_value: string | null;
  value_per_user: string | null;
  assisted_conversions: number | null;
}

export interface ExperimentComparison {
  variant: string;
  against: string;
  conversion_lift: number | null;
  conversion_p_value: number | null;
  average_value_lift: number | null;
  average_value_p_value: number | null;
  enough_data: boolean;
}

export interface ExperimentOverride {
  user_id: string;
  email: string | null;
  name: string | null;
  organization_name: string | null;
  variant: string;
  forced_at: string | null;
}

export type ExperimentUserSource =
  | "forced"
  | "assigned"
  | "not_assigned"
  | "staff"
  | "paused"
  | "rolled_out"
  | (string & {});

export interface ExperimentUser {
  user_id: string;
  email: string;
  name: string | null;
  organization_name: string | null;
  variant: string;
  source: ExperimentUserSource;
  natural_variant: string;
  assigned_at: string | null;
}

export interface ExperimentResults {
  experiment: Experiment;
  overrides: ExperimentOverride[];
  metric: string | null;
  value_currency: string | null;
  groups: ExperimentGroupResult[];
  comparisons: ExperimentComparison[];
  timeline: ExperimentTimelinePoint[];
}

export interface ExperimentTimelinePoint {
  date: string;
  variant: string;
  conversions: number;
  value: string;
}

/** Loads of a window, by origin. Mirrors coloca-nest `CardsLoadsDto`. */
export interface CardsLoads {
  total_usd: string;
  wallet_usd: string;
  crypto_usd: string;
  pesos_usd: string;
  count: number;
}

/** One window of the card program. Mirrors coloca-nest `CardsPeriodDto`. */
export interface CardsPeriodMetrics {
  spend_usd: string;
  purchases: number;
  average_ticket_usd: string;
  declined: number;
  approval_rate: number | null;
  active_cards: number;
  active_cardholders: number;
  new_cardholders: number;
  issued_cards: number;
  loads: CardsLoads;
}

/** Percent changes; `approval_rate` in percentage points. */
export interface CardsMetricsChange {
  spend: number | null;
  purchases: number | null;
  average_ticket: number | null;
  approval_rate: number | null;
  active_cards: number | null;
  active_cardholders: number | null;
  loads: number | null;
  issued_cards: number | null;
}

export interface CardsDay {
  day: string;
  approved_usd: string;
  approved: number;
  declined_usd: string;
  declined: number;
}

export interface CardsMerchant {
  name: string;
  mcc: string | null;
  spend_usd: string;
  purchases: number;
  share: number | null;
  cardholders: number;
}

export interface CardsMcc {
  mcc: string | null;
  spend_usd: string;
  purchases: number;
}

export interface CardsCardholder {
  identity_id: string | null;
  name: string;
  organization_id: string | null;
  organization_name: string | null;
  cards: number;
  spend_usd: string;
  purchases: number;
  average_ticket_usd: string;
  declined: number;
  last_purchase_at: string | null;
}

export interface CardsOrganization {
  organization_id: string;
  name: string | null;
  active_cards: number;
  spend_usd: string;
  previous_spend_usd: string;
  change: number | null;
  approval_rate: number | null;
}

export interface CardsDeclineReason {
  category: string;
  count: number;
  share: number | null;
}

export interface CardsMonth {
  month: string;
  spend_usd: string;
  purchases: number;
  active_cards: number;
}

/** An organization by what it holds in card money now: card wallet and pockets, plus its cards. */
export interface CardsBalanceHolder {
  organization_id: string;
  name: string | null;
  wallet_usd: string;
  cards_usd: string;
  total_usd: string;
  share: number | null;
  cards_with_balance: number;
}

/** One organization's spend per month (`organization_id` null: every other organization). */
export interface CardsSpendShareRow {
  organization_id: string | null;
  name: string | null;
  total_usd: string;
  spend_usd: string[];
  shares: (number | null)[];
}

export interface CardsSpendShares {
  months: string[];
  totals_usd: string[];
  organizations: CardsSpendShareRow[];
  others_count: number;
}

/** `GET /internal/metrics/cards`. Mirrors coloca-nest `CardsMetricsDto`. */
export interface CardsMetrics {
  days: number;
  current: OtcMetricsWindow;
  previous: OtcMetricsWindow;
  currency: string;
  organization: MetricsOrganizationRef | null;
  figures: { current: CardsPeriodMetrics; previous: CardsPeriodMetrics; change: CardsMetricsChange };
  holdings: { balance_usd: string; cards_with_balance: number };
  daily: CardsDay[];
  merchants: CardsMerchant[];
  mcc: CardsMcc[];
  cardholders: CardsCardholder[];
  organizations: CardsOrganization[];
  decline_reasons: CardsDeclineReason[];
  monthly: CardsMonth[];
  /** Across organizations only: null when the read is scoped to one. */
  balance_holders: CardsBalanceHolder[] | null;
  spend_shares: CardsSpendShares | null;
}

export const ONBOARDING_STEPS = [
  "registered",
  "email_verified",
  "kyc_started",
  "kyc_approved",
  "first_transaction",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export type OnboardingStage =
  | "registered"
  | "email_verified"
  | "kyc_started"
  | "kyc_in_review"
  | "kyc_rejected"
  | "kyc_approved"
  | "first_transaction";

export type FirstProduct = "payin" | "payout" | "otc_buy" | "otc_sell" | "card";

/** One cohort: the organizations created in a window and how far they got. Mirrors `UsersPeriodDto`. */
export interface UsersPeriodMetrics {
  registered: number;
  email_verified: number;
  kyc_started: number;
  kyc_approved: number;
  kyc_rejected: number;
  first_transaction: number;
  active_organizations: number;
}

export interface UsersMetricsChange {
  registered: number | null;
  kyc_started: number | null;
  kyc_approved: number | null;
  first_transaction: number | null;
  active_organizations: number | null;
}

export interface UsersFunnelStep {
  step: OnboardingStep;
  count: number;
  of_registered: number | null;
  of_previous_step: number | null;
}

export interface UsersDuration {
  from: OnboardingStep;
  to: OnboardingStep;
  p50_seconds: number | null;
  p90_seconds: number | null;
  samples: number;
}

export interface UsersRecent {
  organization_id: string;
  name: string | null;
  email: string | null;
  registered_at: string;
  stage: OnboardingStage;
  stage_since: string | null;
  last_login_at: string | null;
}

export interface UsersAttention {
  verified_without_transaction: number;
  verified_without_transaction_days: number;
  kyc_in_review_stale: number;
  kyc_in_review_days: number;
}

/** What an organization did with Coloca, one product each. Mirrors coloca-nest `ACTIVITY_PRODUCTS`. */
export const ACTIVITY_PRODUCTS = [
  "collection",
  "payout",
  "otc_buy",
  "otc_sell",
  "card_top_up",
  "card",
] as const;
export type ActivityProduct = (typeof ACTIVITY_PRODUCTS)[number];

export interface UsersRegistrationDay {
  date: string;
  previous_date: string;
  current: number;
  previous: number;
}

export interface OrganizationMover {
  organization_id: string;
  name: string | null;
  current_usd: string;
  previous_usd: string;
  difference_usd: string;
  change: number | null;
  current_operations: number;
  previous_operations: number;
  last_operation_at: string | null;
  products: ActivityProduct[];
}

export interface UsersMovers {
  drop_threshold: number;
  growth_floor_usd: string;
  falling_count: number;
  growing_count: number;
  falling: OrganizationMover[];
  growing: OrganizationMover[];
}

export interface OrganizationInactive {
  organization_id: string;
  name: string | null;
  last_operation_at: string;
  days_inactive: number;
  lookback_usd: string;
  lookback_operations: number;
  products: ActivityProduct[];
}

export interface UsersInactive {
  days: number;
  lookback_days: number;
  count: number;
  organizations: OrganizationInactive[];
}

export interface ProductUsage {
  product: ActivityProduct;
  used_ever: number;
  used_recently: number;
}

export interface OrganizationProducts {
  organization_id: string;
  name: string | null;
  lookback_usd: string;
  used: ActivityProduct[];
  recent: ActivityProduct[];
  never: ActivityProduct[];
}

export interface UsersProducts {
  lookback_days: number;
  organizations: number;
  usage: ProductUsage[];
  by_organization: OrganizationProducts[];
}

/** `GET /internal/metrics/users`. Mirrors coloca-nest `UsersMetricsDto`. */
export interface UsersMetrics {
  days: number;
  current: OtcMetricsWindow;
  previous: OtcMetricsWindow;
  figures: { current: UsersPeriodMetrics; previous: UsersPeriodMetrics; change: UsersMetricsChange };
  funnel: UsersFunnelStep[];
  durations: UsersDuration[];
  first_products: { product: FirstProduct; count: number }[];
  recent: UsersRecent[];
  attention: UsersAttention;
  registrations_daily: UsersRegistrationDay[];
  movers: UsersMovers;
  inactive: UsersInactive;
  products: UsersProducts;
}

export const PLATFORM_AUDIENCES = ["clients", "staff"] as const;
export type PlatformAudience = (typeof PLATFORM_AUDIENCES)[number];

export const PLATFORM_DEVICES = ["desktop_web", "mobile_web", "app", "unknown"] as const;
export type PlatformDevice = (typeof PLATFORM_DEVICES)[number];

export interface PlatformPeriodMetrics {
  active_users: number;
  organizations: number;
  sessions: number;
  visits: number;
  active_ms: number;
  sessions_per_user: number | null;
  session_p50_ms: number | null;
  session_avg_ms: number | null;
  logins: number;
  login_users: number;
  daily_active_users: number | null;
}

export interface PlatformMetricsChange {
  active_users: number | null;
  sessions: number | null;
  session_p50: number | null;
  logins: number | null;
  daily_active_users: number | null;
}

export interface PlatformDay {
  date: string;
  active_users: number;
  sessions: number;
  active_ms: number;
  logins: number;
}

export interface PlatformSectionUse {
  section: PlatformSection;
  visits: number;
  users: number;
  active_ms: number;
  share: number | null;
  previous_active_ms: number;
  change: number | null;
}

export interface PlatformPageUse {
  page: string;
  section: PlatformSection;
  visits: number;
  users: number;
  active_ms: number;
  share: number | null;
}

/** One weekday × hour cell in Colombia; `weekday` is ISO (1 Monday … 7 Sunday). */
export interface PlatformHeatCell {
  weekday: number;
  hour: number;
  active_ms: number;
  visits: number;
  logins: number;
}

/** `GET /internal/metrics/platform`. Mirrors coloca-nest `PlatformMetricsDto`. */
export interface PlatformMetrics {
  days: number;
  current: OtcMetricsWindow;
  previous: OtcMetricsWindow;
  audience: PlatformAudience;
  tracking_since: string | null;
  figures: { current: PlatformPeriodMetrics; previous: PlatformPeriodMetrics; change: PlatformMetricsChange };
  weekly_active_users: number;
  daily: PlatformDay[];
  sections: PlatformSectionUse[];
  pages: PlatformPageUse[];
  heatmap: PlatformHeatCell[];
  devices: { device: PlatformDevice; sessions: number; users: number }[];
  browsers: { label: string; sessions: number }[];
}
